import { useEffect, useState } from 'react';

import FormRequestBlock from 'components/FormRequestBlock';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserInfo, selectUserInfoLoading } from 'app/store/user';
import { maskApiKey, trackSegmentEvent } from 'app/utils';
import config from 'config/constants';
import { Button } from 'components/ui/button';
import { CopyableField } from 'components/ui/copyable-field';
import { Typography } from 'components/ui/typography';
import { regenerateApiKey, revokeSecondaryApiKey } from 'app/graphql/admin-api';
import { toast } from 'sonner';

import { ACTION, SOURCE } from 'app/analytics';
import { env } from 'config/env';
import { Key, RefreshCw, Trash2 } from 'lucide-react';
import { canWrite } from 'app/access';

interface CheckoutIntentApiKeyProps {
  footer?: React.ReactNode;
  flushFooter?: boolean;
}

const CheckoutIntentApiKey = ({ footer, flushFooter }: CheckoutIntentApiKeyProps) => {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector(selectUserInfo);
  const { apiKey = '', apiKeySecondary } = userInfo || {};
  const canManageCheckoutIntentKeys = canWrite(userInfo, 'checkout_intents');
  const isUserInfoLoading = useAppSelector(selectUserInfoLoading);
  const [formLoading, setFormLoading] = useState(!apiKey);
  const [rotating, setRotating] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const handleOnSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    setFormLoading(!apiKey && isUserInfoLoading);
  }, [apiKey, isUserInfoLoading]);

  const handleRotate = async () => {
    if (!canManageCheckoutIntentKeys) {
      return;
    }

    setRotating(true);
    try {
      const { data } = await regenerateApiKey();
      const result = data?.regenerateApiKey;
      if (result?.error) {
        toast.error(result.error);
      } else if (result?.data?.user) {
        dispatch({
          type: 'user/update',
          payload: {
            apiKey: result.data.user.apiKey,
            apiKeySecondary: result.data.user.apiKeySecondary ?? null,
          },
        });
        toast.success('API key rotated');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to rotate API key');
    } finally {
      setRotating(false);
    }
  };

  const handleRevoke = async () => {
    if (!canManageCheckoutIntentKeys) {
      return;
    }

    setRevoking(true);
    try {
      const { data } = await revokeSecondaryApiKey();
      const result = data?.revokeSecondaryApiKey;
      if (result?.error) {
        toast.error(result.error);
      } else if (result?.data?.user) {
        dispatch({
          type: 'user/update',
          payload: {
            apiKey: result.data.user.apiKey,
            apiKeySecondary: result.data.user.apiKeySecondary ?? null,
          },
        });
        toast.success('Old key revoked');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke old key');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <FormRequestBlock
      title="Checkout Intent API key"
      text={
        <>
          <p>
            To learn how to get started, check out the{' '}
            <Typography variant="link" asChild>
              <a href={config.external.doc.quickStartV2} target="_blank" rel="noreferrer noopener">
                Checkout Intent Quick Start Guide
              </a>
            </Typography>
            , or explore the{' '}
            <Typography variant="link" asChild>
              <a href={config.external.doc.homeV2} target="_blank" rel="noreferrer noopener">
                Checkout Intent API documentation
              </a>
            </Typography>
            .
          </p>
          <p>
            The Checkout Intent API endpoint for the {env.VITE_NODE_ENV} environment is:{' '}
            <code>{config.external.checkoutIntent}</code>.
          </p>
        </>
      }
      loading={formLoading}
      onSubmit={handleOnSubmit}
      footer={footer}
      flushFooter={flushFooter}
    >
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-stretch gap-2">
          <CopyableField
            label="API key:"
            value={apiKey}
            mask={maskApiKey}
            icon={<Key className="h-4 w-4" />}
            onCopy={() =>
              trackSegmentEvent(
                SOURCE.API_KEY_MODULE,
                ACTION.CLICK,
                'copy_checkout_intent_api_key_button',
              )
            }
            copyButtonTitle="Copy API key"
          />
          <Button
            variant="outline"
            disabled={rotating || !canManageCheckoutIntentKeys}
            onClick={handleRotate}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {rotating ? 'Rotating…' : 'Rotate'}
          </Button>
        </div>

        {/* When the user has just rotated, the previous key stays valid for a
            grace window so existing clients keep working. Show it inline so
            they can copy it into legacy systems before revoking. */}
        {apiKeySecondary && (
          <div className="flex items-stretch gap-2">
            <CopyableField
              label="Old key (still active):"
              value={apiKeySecondary}
              mask={maskApiKey}
              icon={<Key className="h-4 w-4" />}
              copyButtonTitle="Copy old API key"
            />
            <Button
              variant="destructive"
              disabled={revoking || !canManageCheckoutIntentKeys}
              onClick={handleRevoke}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {revoking ? 'Revoking…' : 'Revoke'}
            </Button>
          </div>
        )}
      </div>
    </FormRequestBlock>
  );
};

export default CheckoutIntentApiKey;
